import { useHudStore } from "../store/hudStore";
import { VAULT_ROOT } from "../data/seed";
import Page from "../components/Page";
import VaultTree from "../components/vault/VaultTree";
import NoteBlock from "../components/vault/NoteBlock";
import { Badge, Button, Card, Field } from "../design-system/components";
import "./character-vault.css";

// Obsidian vault browser (app.html screenVault, 1181–1220). The search box is a
// controlled input, so typing no longer triggers a full re-render. The caret and
// scroll survive (this is the interaction bug that started the rewrite).
export default function Vault() {
  const st = useHudStore();
  const note = st.vaultLive && st.noteId ? st.vaultNotes?.[st.noteId] ?? null : null;

  // Why the reading pane is empty. These used to collapse into one message
  // ("not in the synced vault yet"), which reported a broken endpoint as a sync
  // gap and sent you looking at Remotely Save instead of the API.
  const empty = st.vaultLoading && !st.vaultLive
    ? { fa: "fa-solid fa-spinner fa-spin", bad: false, title: "Loading your vault...",
        body: "Your quests are ready. Notes are loading in the background.", retry: false, obsidian: false }
    : st.vaultFailed && !st.vaultLive
    ? { fa: "fa-solid fa-triangle-exclamation", bad: true, title: "Couldn't reach the vault",
        body: "The vault listing didn't load, so there's nothing to browse. Your notes remain saved.",
        retry: false, obsidian: false }
    : st.noteLoading
    ? { fa: "fa-solid fa-spinner fa-spin", bad: false, title: "Loading the note...",
        body: "Fetching it from the vault.", retry: false, obsidian: false }
    : st.noteFailed
    ? { fa: "fa-solid fa-triangle-exclamation", bad: true, title: "Couldn't load this note",
        body: "The note is in the vault, but fetching it failed. Try again, or open it in Obsidian.",
        retry: true, obsidian: true }
    : st.noteId
    ? { fa: "fa-solid fa-cloud-arrow-down", bad: false, title: "Not in the synced vault yet",
        body: "This note isn't in the vault the HUD can see. Open it in Obsidian, or wait for the next Remotely Save sync.",
        retry: false, obsidian: true }
    : { fa: "fa-regular fa-file-lines", bad: false, title: "No note open",
        body: "Pick a file from the tree, or search.", retry: false, obsidian: false };

  // Only things the browser can actually observe. The previous three lines
  // asserted Remotely Save state, file freshness and DynamoDB agreement, none
  // of which the HUD can see: Remotely Save is an Obsidian desktop plugin.
  const checks = [
    {
      label: "Your changes saved to the server",
      state: st.unsaved.length
        ? st.unsaved.length + " pending"
        : st.saving
          ? "saving"
          : st.live
            ? "all saved"
            : "not connected",
      color: st.unsaved.length ? "var(--danger)" : st.saving ? "var(--orange)" : st.live ? "var(--green)" : "var(--mut)",
    },
    { label: "Reading live data", state: st.live ? "yes" : "no, local only", color: st.live ? "var(--green)" : "var(--mut)" },
    { label: "Vault file sync (Remotely Save)", state: "runs in Obsidian", color: "var(--mut)" },
  ];

  const noteRaw = note
    ? "---\n" +
      Object.keys(note.front).map((k) => k + ": " + (note.front[k] ?? "")).join("\n") +
      "\n---\n\n" +
      (note.blocks || [])
        .map((b) =>
          b.k === "h" ? "## " + (b.text ?? "")
          : b.k === "li" ? "- " + (b.text ?? "")
          : b.k === "task" ? "- [" + (b.done ? "x" : " ") + "] " + (b.text ?? "")
          : b.k === "code" ? "```" + (b.lang ?? "") + "\n" + (b.text ?? "") + "\n```"
          : b.k === "dv" ? "```dataview\n" + (b.query ?? "") + "\n```"
          : b.text ?? "",
        )
        .join("\n\n")
    : "";

  return (
    <Page back="Back to the HUD">
      <Card className="vault-summary-panel">
        <span className="vault-summary-icon" aria-hidden="true"><i className="fa-solid fa-vault" /></span>
        <div className="vault-summary-heading">
          <h1>Vault</h1>
          <Badge>Obsidian vault</Badge>
          <p className="vault-path">{VAULT_ROOT}</p>
        </div>
        <div className="vault-save-checks">
          {checks.map((ck) => <div key={ck.label} className="vault-save-check"><span>{ck.label}</span><Badge style={{ color: ck.color }}>{ck.state}</Badge></div>)}
          {st.unsaved.length > 0 && <Button variant="primary" size="compact" onClick={st.retryUnsaved}>Retry {st.unsaved.length} pending change{st.unsaved.length === 1 ? "" : "s"}</Button>}
          {st.unsaved.some(u => u.detail) && <ul role="status">{st.unsaved.filter(u => u.detail).map(u => <li key={u.key}>{u.label}: {u.detail}</li>)}</ul>}
        </div>
      </Card>

      {/* tree + note */}
      <div className="vault-browser-grid">
        <Card className="vault-tree-panel vault-navigation-panel">
          <Field label="Search the vault" type="search" value={st.vq} onChange={(e) => st.setVq(e.target.value)} placeholder="Search notes or folders" />
          {st.vaultLoading && <p className="vault-tree-state" role="status"><i aria-hidden="true" className="fa-solid fa-spinner fa-spin" />{st.vaultLive ? "Refreshing your notes..." : "Loading your notes..."}</p>}
          {st.vaultFailed && st.vaultLive && <p className="vault-tree-state" role="alert">Your vault could not refresh. Previously loaded notes are still available.</p>}
          {st.vaultFailed && <Button size="compact" onClick={st.retryVault} loading={st.vaultLoading}>Retry vault</Button>}
          {(!st.vaultLoading || st.vaultLive) && <VaultTree />}
        </Card>

        <Card className="vault-reading-panel">
          {note ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <h2 className="vault-note-heading vault-path">{st.noteId || ""}</h2>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Button size="compact" onClick={st.toggleRaw} aria-pressed={st.raw}>{st.raw ? "Rendered" : "Raw"}</Button>
                  <a className="ds-button ds-button--secondary ds-button--compact ds-focusable" href={`obsidian://open?vault=Life%20OS&file=${encodeURIComponent(st.noteId || "")}`}>
                    <span className="ds-icon" aria-hidden="true"><i className="fa-solid fa-arrow-up-right-from-square" /></span>
                    Open in Obsidian
                  </a>
                </div>
              </div>
              {st.raw ? (
                <pre className="vault-raw vault-path">{noteRaw}</pre>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
                  <div className="vault-frontmatter">
                    {Object.keys(note.front).map((k) => (
                      <Badge key={k} className="vault-metadata">
                        <b style={{ color: "var(--mut)", fontWeight: 700 }}>{k}</b>
                        <span className="vault-path" style={{ color: "var(--accent-ink)" }}>{String(note.front[k])}</span>
                      </Badge>
                    ))}
                  </div>
                  {(note.blocks || []).map((b, i) => (
                    <div key={i} style={{ minWidth: 0 }}>
                      <NoteBlock block={b} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="vault-empty" role={empty.bad ? "alert" : "status"} aria-busy={st.noteLoading || (st.vaultLoading && !st.vaultLive)}>
              <span className="vault-empty-icon" style={{ color: empty.bad ? "var(--danger-ink)" : "var(--mut)" }}>
                <i aria-hidden="true" className={empty.fa} />
              </span>
              <h2 className="detail-heading">{empty.title}</h2>
              <p>{empty.body}</p>
              {empty.retry && st.noteId && (
                <Button size="compact" onClick={() => st.openNote(st.noteId!)}>Try again</Button>
              )}
              {empty.obsidian && st.noteId && (
                <a className="ds-button ds-button--secondary ds-button--compact ds-focusable" href={`obsidian://open?vault=Life%20OS&file=${encodeURIComponent(st.noteId)}`}>
                  <span className="ds-icon" aria-hidden="true"><i className="fa-solid fa-arrow-up-right-from-square" /></span> Open in Obsidian
                </a>
              )}
            </div>
          )}
        </Card>
      </div>
    </Page>
  );
}
